import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  collection,
  query,
  getDocs,
  addDoc,
  deleteDoc,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore'
import { User } from 'firebase/auth'
import { db } from '../lib/firebase'

// Types
export interface UserProfile {
  email: string
  displayName: string | null
  photoURL: string | null
  createdAt: Timestamp
  updatedAt: Timestamp
}

export interface UserSettings {
  theme: 'light' | 'dark' | 'system'
  notificationsEnabled: boolean
  // Email fetch settings
  emailRetentionDays: 7 | 30
  autoFetchEnabled: boolean
  autoFetchIntervalMinutes: 3 | 5 | 15
}

// Subscription tiers
export type SubscriptionTier = 'free' | 'pro'

export interface UserSubscription {
  tier: SubscriptionTier
  startedAt?: Timestamp
  expiresAt?: Timestamp
  // For future Stripe integration
  stripeCustomerId?: string
  stripeSubscriptionId?: string
}

// Feature flags based on subscription tier
export const TIER_FEATURES = {
  free: {
    maxAccounts: 1,
    autoRefresh: false,
    autoDelete: false,
    nuke: false,
    retentionDays: [7] as const,
    manualRefresh: true,
  },
  pro: {
    maxAccounts: 5,
    autoRefresh: true,
    autoDelete: true,
    nuke: true,
    retentionDays: [7, 30] as const,
    manualRefresh: true,
  }
} as const

export interface ConnectedAccount {
  id?: string
  email: string
  provider: 'gmail' | 'outlook' | 'hotmail' | 'imap'
  displayName: string
  color: string
  // Credentials for IMAP/SMTP access
  appPassword?: string
  imapServer?: string
  imapPort?: number
  smtpServer?: string
  smtpPort?: number
  // Connection status
  isVerified?: boolean
  lastVerifiedAt?: Timestamp
  addedAt: Timestamp
}

// User Profile Operations
export async function createUserProfile(user: User): Promise<void> {
  const userRef = doc(db, 'users', user.uid)
  const userSnap = await getDoc(userRef)

  if (!userSnap.exists()) {
    await setDoc(userRef, {
      email: user.email,
      displayName: user.displayName,
      photoURL: user.photoURL,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    })

    // Create default settings (free tier defaults)
    const settingsRef = doc(db, 'users', user.uid, 'settings', 'preferences')
    await setDoc(settingsRef, {
      theme: 'system',
      notificationsEnabled: true,
      emailRetentionDays: 7, // Free tier only gets 7 days
      autoFetchEnabled: false, // Free tier doesn't get auto-fetch
      autoFetchIntervalMinutes: 5
    })

    // Create subscription document (default to free tier)
    const subscriptionRef = doc(db, 'users', user.uid, 'settings', 'subscription')
    await setDoc(subscriptionRef, {
      tier: 'free',
      startedAt: serverTimestamp()
    })
  }
}

export async function getUserProfile(userId: string): Promise<UserProfile | null> {
  const userRef = doc(db, 'users', userId)
  const userSnap = await getDoc(userRef)

  if (userSnap.exists()) {
    return userSnap.data() as UserProfile
  }
  return null
}

export async function updateUserProfile(
  userId: string,
  updates: Partial<UserProfile>
): Promise<void> {
  const userRef = doc(db, 'users', userId)
  await updateDoc(userRef, {
    ...updates,
    updatedAt: serverTimestamp()
  })
}

// User Settings Operations
export async function getUserSettings(userId: string): Promise<UserSettings | null> {
  const settingsRef = doc(db, 'users', userId, 'settings', 'preferences')
  const settingsSnap = await getDoc(settingsRef)

  if (settingsSnap.exists()) {
    return settingsSnap.data() as UserSettings
  }
  return null
}

export async function updateUserSettings(
  userId: string,
  settings: Partial<UserSettings>
): Promise<void> {
  const settingsRef = doc(db, 'users', userId, 'settings', 'preferences')
  await updateDoc(settingsRef, settings)
}

// Subscription Operations
export async function getUserSubscription(userId: string): Promise<UserSubscription> {
  const subscriptionRef = doc(db, 'users', userId, 'settings', 'subscription')
  const subscriptionSnap = await getDoc(subscriptionRef)

  if (subscriptionSnap.exists()) {
    return subscriptionSnap.data() as UserSubscription
  }

  // Default to free tier if no subscription exists
  return { tier: 'free' }
}

export async function updateUserSubscription(
  userId: string,
  subscription: Partial<UserSubscription>
): Promise<void> {
  const subscriptionRef = doc(db, 'users', userId, 'settings', 'subscription')
  await setDoc(subscriptionRef, subscription, { merge: true })
}

// Helper to check if user has a specific feature
export function hasFeature(
  tier: SubscriptionTier,
  feature: keyof typeof TIER_FEATURES.free
): boolean {
  return !!TIER_FEATURES[tier][feature]
}

// Connected Accounts Operations
export async function getConnectedAccounts(userId: string): Promise<ConnectedAccount[]> {
  const accountsRef = collection(db, 'users', userId, 'connectedAccounts')
  const q = query(accountsRef)
  const querySnap = await getDocs(q)

  return querySnap.docs.map((doc) => ({
    id: doc.id,
    ...doc.data()
  })) as ConnectedAccount[]
}

export async function addConnectedAccount(
  userId: string,
  account: Omit<ConnectedAccount, 'id' | 'addedAt'>
): Promise<string> {
  const accountsRef = collection(db, 'users', userId, 'connectedAccounts')
  const docRef = await addDoc(accountsRef, {
    ...account,
    addedAt: serverTimestamp()
  })
  return docRef.id
}

export async function removeConnectedAccount(
  userId: string,
  accountId: string
): Promise<void> {
  const accountRef = doc(db, 'users', userId, 'connectedAccounts', accountId)
  await deleteDoc(accountRef)
}
